'use client';
import * as React from 'react';
import { Input } from '@/components/ui/field';
import { PhoneInput } from '@/components/ui/phone-input';

export type AuthIdentifierKind = 'email' | 'phone';

const startsLikePhone = (value: string) => /^[\s+\d(]/.test(value);

/** One identity field for both supported credentials. A phone-shaped first
 * character reveals the explicit country picker, so a national number is never
 * silently assigned to the wrong country. */
/** The phone field on glass, in step with the glass Input. */
export const PHONE_GLASS = 'h-12 rounded-full border-transparent bg-white/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] backdrop-blur-sm pl-2 has-[[aria-invalid=true]]:border-destructive';

export function AuthIdentifierInput({
  name = 'identifier',
  placeholder,
  phonePlaceholder,
  required = false,
  invalid = false,
  glass = false,
  onKindChange,
}: {
  name?: string;
  placeholder: string;
  phonePlaceholder: string;
  required?: boolean;
  invalid?: boolean;
  glass?: boolean;
  onKindChange?: (kind: AuthIdentifierKind) => void;
}) {
  const [kind, setKind] = React.useState<AuthIdentifierKind>('email');
  const [email, setEmail] = React.useState('');
  const [phoneSeed, setPhoneSeed] = React.useState('');

  if (kind === 'phone') {
    return (
      <div data-auth-identifier-kind="phone">
        <PhoneInput
          name={name}
          defaultValue={phoneSeed}
          placeholder={phonePlaceholder}
          aria-label={placeholder}
          aria-invalid={invalid || undefined}
          containerClassName={glass ? PHONE_GLASS : undefined}
          required={required}
          autoFocus
          onRawChange={(value) => {
            if (value) return;
            setPhoneSeed('');
            setKind('email');
            onKindChange?.('email');
          }}
        />
      </div>
    );
  }

  return (
    <div data-auth-identifier-kind="email">
      <Input
        name={name}
        icon="user"
        type="text"
        inputMode="email"
        autoComplete="username"
        value={email}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-invalid={invalid || undefined}
        variant={glass ? 'glass' : 'default'}
        required={required}
        onChange={(event) => {
          const value = event.target.value;
          if (value && startsLikePhone(value)) {
            setPhoneSeed(value);
            setKind('phone');
            onKindChange?.('phone');
            return;
          }
          setEmail(value);
        }}
      />
    </div>
  );
}
